import {
  faChevronLeft,
  faChevronRight
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useT } from "@repo/transifex";
import { cn } from "~/lib/utils";

export type CollectPaginationProps = {
  /** 1-indexed current page. */
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
};

type PageItem =
  | { type: "page"; page: number }
  | { type: "ellipsis"; key: string };

// Pages shown around the current page on each side.
const WINDOW = 2;
// Max pages before switching to windowed (ellipsis) mode: first + last + current ± WINDOW + 2 possible ellipses.
const WINDOWED_THRESHOLD = 1 + 1 + (WINDOW * 2 + 1) + 2;

/**
 * Build a windowed list of page tokens: first, last, the current page ±WINDOW,
 * and ellipses for the gaps. Shows every page when there are few.
 */
export const buildPageItems = (
  currentPage: number,
  totalPages: number
): PageItem[] => {
  const shown = new Set<number>();
  if (totalPages <= WINDOWED_THRESHOLD) {
    for (let page = 1; page <= totalPages; page++) shown.add(page);
  } else {
    [
      1,
      totalPages,
      ...Array.from(
        { length: WINDOW * 2 + 1 },
        (_, i) => currentPage - WINDOW + i
      )
    ].forEach((page) => {
      if (page >= 1 && page <= totalPages) shown.add(page);
    });
  }

  const sorted = Array.from(shown).sort((a, b) => a - b);
  const items: PageItem[] = [];
  let previous = 0;
  sorted.forEach((page) => {
    if (page - previous > 1)
      items.push({ type: "ellipsis", key: `gap-${page}` });
    items.push({ type: "page", page });
    previous = page;
  });
  return items;
};

const arrowClassName = (disabled: boolean) =>
  cn(
    "flex size-9 items-center justify-center rounded-lg bg-interface-neutral-2 text-interface-neutral-6 transition-colors hover:bg-interface-neutral-3",
    { "opacity-30": disabled }
  );

/**
 * Pagination control with ellipsis windowing, styled for the collect redesign.
 * Controlled and 1-indexed - drop-in compatible with the app's existing
 * `Pagination` API, but adds windowing for long page ranges.
 */
export const CollectPagination = ({
  currentPage,
  totalPages,
  onPageChange,
  className
}: CollectPaginationProps) => {
  const t = useT();

  if (totalPages <= 1) return null;

  const items = buildPageItems(currentPage, totalPages);
  const atStart = currentPage <= 1;
  const atEnd = currentPage >= totalPages;

  return (
    <div className={cn("flex select-none items-center gap-1.5", className)}>
      <button
        type="button"
        aria-label={t("Previous page")}
        disabled={atStart}
        onClick={() => {
          onPageChange(currentPage - 1);
        }}
        className={arrowClassName(atStart)}
      >
        <FontAwesomeIcon icon={faChevronLeft} />
      </button>

      {items.map((item) =>
        item.type === "ellipsis" ? (
          <span
            key={item.key}
            className="w-5 text-center font-bold text-interface-neutral-4"
          >
            …
          </span>
        ) : (
          <button
            key={`page-${item.page}`}
            type="button"
            aria-current={item.page === currentPage ? "page" : undefined}
            onClick={() => {
              onPageChange(item.page);
            }}
            className={cn(
              "flex h-9 min-w-9 items-center justify-center rounded-lg px-2 text-sm font-bold transition-colors",
              {
                "bg-interface-neutral-6 text-interface-neutral-0":
                  item.page === currentPage,
                "bg-interface-neutral-2 text-interface-neutral-6 hover:bg-interface-neutral-3":
                  item.page !== currentPage
              }
            )}
          >
            {item.page}
          </button>
        )
      )}

      <button
        type="button"
        aria-label={t("Next page")}
        disabled={atEnd}
        onClick={() => {
          onPageChange(currentPage + 1);
        }}
        className={arrowClassName(atEnd)}
      >
        <FontAwesomeIcon icon={faChevronRight} />
      </button>
    </div>
  );
};
